/**
 * WP6/V2 / WAVE5-W4 — lokalne NPC-pieski na mapie Town (wander + pet + dymek).
 * Używa WalkingDog (@deprecated alias → AnimatedDogAvatar).
 * Tap → polska kwestia w dymku (rotacja) + lektor; dymek ~2.5 s, nie blokuje ruchu.
 */
import Phaser from 'phaser';
import { petNpc, tickNpc, type NpcState } from '@pieskowo/shared';
import { narrator } from '../../core/Narrator.js';
import { TOWN_BOUNDS, TOWN_NPC_SPAWNS } from './hotspots.js';
import { WalkingDog } from './WalkingDog.js';

/** Miłe kwestie ulicznych piesków (rotacja). */
const DOG_LINES_PL: readonly string[] = [
  'Hau! Cześć, nowy kolego!',
  'Głaszcz dalej, proszę!',
  'Lubię spacery po mieście!',
  'Masz może smakołyk?',
  'Hau hau! Bawimy się?',
];

const BUBBLE_MS = 2500;

type DogVisual = {
  state: NpcState;
  root: Phaser.GameObjects.Container;
  walk: WalkingDog | null;
  body: Phaser.GameObjects.Arc | null;
  heart?: Phaser.GameObjects.Text;
  bubble?: Phaser.GameObjects.Text;
  bubbleTimer?: Phaser.Time.TimerEvent;
  lineIdx: number;
  color: number;
  prevX: number;
};

export class LocalDogs {
  private dogs: DogVisual[] = [];

  constructor(private readonly scene: Phaser.Scene) {}

  spawn(): void {
    for (const s of TOWN_NPC_SPAWNS) {
      const state: NpcState = {
        id: s.id,
        x: s.x,
        y: s.y,
        phase: 'idle',
        phaseUntil: Date.now() + 500 + Math.random() * 1000,
        targetX: s.x,
        targetY: s.y,
        emote: '',
      };

      const parts: Phaser.GameObjects.GameObject[] = [];
      parts.push(this.scene.add.ellipse(0, 26, 40, 14, 0x000000, 0.14));

      let walk: WalkingDog | null = null;
      let body: Phaser.GameObjects.Arc | null = null;
      // AnimatedDogAvatar (przez alias) zawsze dostępny — placeholdery w DogAvatar
      walk = new WalkingDog(this.scene, { breed: 'maltese', size: 60, tint: s.color });
      parts.push(walk.image);

      const hit = this.scene.add
        .circle(0, 0, 36, 0xffffff, 0.001)
        .setInteractive({ useHandCursor: true });
      parts.push(hit);

      const root = this.scene.add.container(state.x, state.y, parts);
      root.setSize(72, 72);
      root.setDepth(50);

      // WalkingDog.image to osobny kontener — trzymamy go w root jako child
      walk.image.setPosition(0, 0);

      const dog: DogVisual = {
        state,
        root,
        walk,
        body,
        color: s.color,
        prevX: state.x,
        lineIdx: Math.floor(Math.random() * DOG_LINES_PL.length),
      };
      hit.on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event?.stopPropagation?.();
        this.onPet(dog);
      });

      this.dogs.push(dog);
    }
  }

  private onPet(dog: DogVisual): void {
    dog.state = petNpc(dog.state);

    const line = DOG_LINES_PL[dog.lineIdx % DOG_LINES_PL.length]!;
    dog.lineIdx = (dog.lineIdx + 1) % DOG_LINES_PL.length;
    this.showBubble(dog, line);
    narrator.sayText(line, { immediate: true });

    dog.heart?.destroy();
    dog.heart = this.scene.add
      .text(0, -40, '💖', { fontSize: '28px' })
      .setOrigin(0.5);
    dog.root.add(dog.heart);
    this.scene.tweens.add({
      targets: dog.heart,
      y: -70,
      alpha: 0,
      duration: 700,
      onComplete: () => {
        dog.heart?.destroy();
        dog.heart = undefined;
      },
    });
  }

  private showBubble(dog: DogVisual, text: string): void {
    dog.bubbleTimer?.remove(false);
    dog.bubbleTimer = undefined;
    dog.bubble?.destroy();
    dog.bubble = undefined;

    const bubble = this.scene.add
      .text(0, -56, text, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffee',
        padding: { x: 8, y: 4 },
        align: 'center',
        wordWrap: { width: 150 },
      })
      .setOrigin(0.5);
    bubble.disableInteractive();
    dog.root.add(bubble);
    dog.bubble = bubble;

    dog.bubbleTimer = this.scene.time.delayedCall(BUBBLE_MS, () => {
      if (dog.bubble === bubble) {
        bubble.destroy();
        dog.bubble = undefined;
      }
      dog.bubbleTimer = undefined;
    });
  }

  update(dtSec: number): void {
    for (const dog of this.dogs) {
      dog.state = tickNpc(dog.state, dtSec, TOWN_BOUNDS);
      dog.root.setPosition(dog.state.x, dog.state.y);
      const dx = dog.state.x - dog.prevX;
      dog.prevX = dog.state.x;
      const moving = Math.abs(dx) > 0.15;
      // WalkingDog.image jest w root — update animacji w lokalnych 0,0
      if (dog.walk) {
        dog.walk.image.setPosition(0, 0);
        dog.walk.update(dtSec, moving, dx);
        dog.walk.image.setPosition(0, 0);
      }
      if (dog.state.emote === 'heart' && !dog.heart) {
        dog.root.setScale(1.08);
      } else if (dog.state.phase !== 'react') {
        dog.root.setScale(1);
      }
    }
  }

  destroy(): void {
    for (const d of this.dogs) {
      d.bubbleTimer?.remove(false);
      d.bubble?.destroy();
      d.walk?.destroy();
      d.root.destroy(true);
    }
    this.dogs = [];
  }
}
